import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  LogOut, 
  Inbox, 
  Clock, 
  CheckCircle, 
  XCircle,
  Filter,
  Trash2,
  Eye,
  RefreshCw,
  User,
  Mail,
  Phone,
  Building2,
  MessageSquare,
  Calendar,
  MapPin,
  Wrench
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, contacted: 0, closed: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    intent: '',
    property_type: ''
  });

  const getToken = () => localStorage.getItem('token');

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser || !getToken()) {
      navigate('/admin/login');
      return;
    }
    setUser(JSON.parse(storedUser));
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([fetchSubmissions(), fetchStats()]);
    } catch (error) {
      if (error.message === 'Unauthorized') {
        handleLogout();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSubmissions = async () => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.intent) params.append('intent', filters.intent);
    if (filters.property_type) params.append('property_type', filters.property_type);

    const response = await fetch(`${API_URL}/api/admin/submissions?${params}`, {
      headers: { 'Authorization': `Bearer ${getToken()}` }
    });

    if (response.status === 401) throw new Error('Unauthorized');
    if (!response.ok) throw new Error('Failed to fetch submissions');

    const data = await response.json();
    setSubmissions(data);
  };

  const fetchStats = async () => {
    const response = await fetch(`${API_URL}/api/admin/stats`, {
      headers: { 'Authorization': `Bearer ${getToken()}` }
    });

    if (response.status === 401) throw new Error('Unauthorized');
    if (!response.ok) throw new Error('Failed to fetch stats');

    const data = await response.json();
    setStats(data);
  };

  const updateSubmissionStatus = async (id, status) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/submissions/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({ status })
      });

      if (!response.ok) throw new Error('Failed to update');

      toast.success(`Status updated to ${status}`);
      fetchData();
      if (selectedSubmission?.id === id) {
        setSelectedSubmission(prev => ({ ...prev, status }));
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteSubmission = async (id) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/submissions/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });

      if (!response.ok) throw new Error('Failed to delete');

      toast.success('Submission deleted');
      setSelectedSubmission(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete submission');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/admin/login');
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value === 'all' ? '' : value }));
  };

  useEffect(() => {
    if (user) fetchSubmissions();
  }, [filters]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-amber-100 text-amber-800';
      case 'contacted': return 'bg-blue-100 text-blue-800';
      case 'closed': return 'bg-green-100 text-green-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const getIntentLabel = (intent) => {
    switch (intent) {
      case 'assessment': return 'Site Assessment';
      case 'quote': return 'Service Quote';
      case 'consultation': return 'Consultation';
      case 'general': return 'General Inquiry';
      default: return intent;
    }
  };

  const getPropertyTypeLabel = (type) => {
    switch (type) {
      case 'residential': return 'Private Residence';
      case 'hoa': return 'HOA / Multi-Family';
      case 'corporate': return 'Corporate Campus';
      case 'retail': return 'Retail Center';
      case 'hospitality': return 'Hotel';
      case 'education': return 'Educational Facility';
      default: return type || 'Not specified';
    }
  };

  const getServiceLabel = (service) => {
    switch (service) {
      case 'landscape': return 'Landscape Maintenance';
      case 'irrigation': return 'Irrigation Management';
      case 'pressure-washing': return 'Pressure Washing';
      case 'exterior': return 'Exterior Asset Care';
      default: return service || 'Not specified';
    }
  };

  const getSourceLabel = (source) => {
    if (!source) return 'Direct / Not specified';
    
    // QR Code landing pages
    const qrSources = {
      'qr-pasadena-mailer': 'QR Code → Pasadena Mailer',
      'qr-lacanada-mailer': 'QR Code → La Cañada Flintridge Mailer',
      'qr-sanmarino-mailer': 'QR Code → San Marino Mailer',
      'qr-arcadia-mailer': 'QR Code → Arcadia Mailer'
    };
    
    if (qrSources[source]) {
      return qrSources[source];
    }
    
    // Parse source like "industries-residential" or "service-landscape"
    const parts = source.split('-');
    const type = parts[0];
    const detail = parts.slice(1).join('-');
    
    if (type === 'industries') {
      const industryLabels = {
        'residential': 'Industries → Private Residences',
        'hoa': 'Industries → HOA / Multi-Family',
        'corporate': 'Industries → Corporate Campuses',
        'retail': 'Industries → Retail Centers'
      };
      return industryLabels[detail] || `Industries → ${detail}`;
    }
    
    if (type === 'service') {
      const serviceLabels = {
        'landscape': 'Service Page → Landscape Maintenance',
        'irrigation': 'Service Page → Irrigation Management',
        'pressure-washing': 'Service Page → Pressure Washing',
        'exterior': 'Service Page → Exterior Asset Care',
        'exterior-asset-care': 'Service Page → Exterior Asset Care'
      };
      return serviceLabels[detail] || `Service Page → ${detail}`;
    }
    
    if (type === 'qr') {
      return `QR Code → ${detail.charAt(0).toUpperCase() + detail.slice(1).replace(/-/g, ' ')}`;
    }
    
    return source;
  };

  if (isLoading && !user) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-[#1a2e44] text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <h1 className="text-lg font-bold">Sunlight Grounds Care - Manager Dashboard</h1>
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-300">{user?.email}</span>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-sm hover:text-slate-300 transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-6 border border-slate-200">
            <div className="flex items-center gap-3">
              <Inbox className="w-8 h-8 text-slate-400" />
              <div>
                <p className="text-2xl font-bold text-[#1a2e44]">{stats.total}</p>
                <p className="text-sm text-slate-600">Total</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 border border-slate-200">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-amber-500" />
              <div>
                <p className="text-2xl font-bold text-[#1a2e44]">{stats.pending}</p>
                <p className="text-sm text-slate-600">Pending</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 border border-slate-200">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold text-[#1a2e44]">{stats.contacted}</p>
                <p className="text-sm text-slate-600">Contacted</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 border border-slate-200">
            <div className="flex items-center gap-3">
              <XCircle className="w-8 h-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold text-[#1a2e44]">{stats.closed}</p>
                <p className="text-sm text-slate-600">Closed</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 border border-slate-200 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-700">Filters:</span>
            </div>
            
            <Select value={filters.status || 'all'} onValueChange={(v) => handleFilterChange('status', v)}>
              <SelectTrigger className="w-[140px]" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filters.intent || 'all'} onValueChange={(v) => handleFilterChange('intent', v)}>
              <SelectTrigger className="w-[160px]" data-testid="filter-intent">
                <SelectValue placeholder="Inquiry Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="assessment">Site Assessment</SelectItem>
                <SelectItem value="quote">Service Quote</SelectItem>
                <SelectItem value="consultation">Consultation</SelectItem>
                <SelectItem value="general">General Inquiry</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filters.property_type || 'all'} onValueChange={(v) => handleFilterChange('property_type', v)}>
              <SelectTrigger className="w-[180px]" data-testid="filter-property">
                <SelectValue placeholder="Property Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Properties</SelectItem>
                <SelectItem value="residential">Private Residence</SelectItem>
                <SelectItem value="hoa">HOA / Multi-Family</SelectItem>
                <SelectItem value="corporate">Corporate Campus</SelectItem>
                <SelectItem value="retail">Retail Center</SelectItem>
                <SelectItem value="hospitality">Hotel</SelectItem>
                <SelectItem value="education">Educational Facility</SelectItem>
              </SelectContent>
            </Select>

            <button
              onClick={fetchData}
              className="flex items-center gap-2 text-sm text-[#1a2e44] hover:text-[#0f1d2d] ml-auto"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Submissions List */}
          <div className="lg:col-span-2 bg-white border border-slate-200">
            <div className="p-4 border-b border-slate-200">
              <h2 className="font-bold text-[#1a2e44]">Form Submissions ({submissions.length})</h2>
            </div>
            
            {isLoading ? (
              <div className="p-8 text-center text-slate-500">Loading submissions...</div>
            ) : submissions.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No submissions found</div>
            ) : (
              <div className="divide-y divide-slate-200 max-h-[600px] overflow-y-auto">
                {submissions.map((submission) => (
                  <div
                    key={submission.id}
                    onClick={() => setSelectedSubmission(submission)}
                    className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                      selectedSubmission?.id === submission.id ? 'bg-slate-50 border-l-4 border-l-[#1a2e44]' : ''
                    }`}
                    data-testid={`submission-${submission.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-[#1a2e44] truncate">{submission.name}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(submission.status)}`}>
                            {submission.status}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 truncate">{submission.email}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-xs text-slate-400">{getIntentLabel(submission.intent)}</p>
                          {submission.source && (
                            <>
                              <span className="text-slate-300">•</span>
                              <p className="text-xs text-[#1a2e44] font-medium">{getSourceLabel(submission.source).split('→')[0].trim()}</p>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-400">{formatDate(submission.created_at)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submission Detail */}
          <div className="bg-white border border-slate-200">
            <div className="p-4 border-b border-slate-200">
              <h2 className="font-bold text-[#1a2e44]">Submission Details</h2>
            </div>
            
            {selectedSubmission ? (
              <div className="p-4">
                <div className="space-y-4">
                  {/* Status Actions */}
                  <div className="flex flex-wrap gap-2 pb-4 border-b border-slate-200">
                    <button
                      onClick={() => updateSubmissionStatus(selectedSubmission.id, 'pending')}
                      className={`text-xs px-3 py-1.5 rounded transition-colors ${
                        selectedSubmission.status === 'pending' 
                          ? 'bg-amber-500 text-white' 
                          : 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                      }`}
                      data-testid="status-pending"
                    >
                      Pending
                    </button>
                    <button
                      onClick={() => updateSubmissionStatus(selectedSubmission.id, 'contacted')}
                      className={`text-xs px-3 py-1.5 rounded transition-colors ${
                        selectedSubmission.status === 'contacted' 
                          ? 'bg-blue-500 text-white' 
                          : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                      }`}
                      data-testid="status-contacted"
                    >
                      Contacted
                    </button>
                    <button
                      onClick={() => updateSubmissionStatus(selectedSubmission.id, 'closed')}
                      className={`text-xs px-3 py-1.5 rounded transition-colors ${
                        selectedSubmission.status === 'closed' 
                          ? 'bg-green-500 text-white' 
                          : 'bg-green-100 text-green-800 hover:bg-green-200'
                      }`}
                      data-testid="status-closed"
                    >
                      Closed
                    </button>
                    <button
                      onClick={() => deleteSubmission(selectedSubmission.id)}
                      className="text-xs px-3 py-1.5 rounded bg-red-100 text-red-800 hover:bg-red-200 ml-auto flex items-center gap-1"
                      data-testid="delete-submission"
                    >
                      <Trash2 className="w-3 h-3" />
                      Delete
                    </button>
                  </div>

                  {/* Contact Info */}
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <User className="w-4 h-4 text-slate-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-slate-500">Name</p>
                        <p className="text-sm font-medium text-[#1a2e44]">{selectedSubmission.name}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Mail className="w-4 h-4 text-slate-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-slate-500">Email</p>
                        <a href={`mailto:${selectedSubmission.email}`} className="text-sm font-medium text-[#1a2e44] hover:underline">
                          {selectedSubmission.email}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Phone className="w-4 h-4 text-slate-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-slate-500">Phone</p>
                        <a href={`tel:${selectedSubmission.phone}`} className="text-sm font-medium text-[#1a2e44] hover:underline">
                          {selectedSubmission.phone}
                        </a>
                      </div>
                    </div>

                    {selectedSubmission.company && (
                      <div className="flex items-start gap-3">
                        <Building2 className="w-4 h-4 text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-500">Company / Property</p>
                          <p className="text-sm font-medium text-[#1a2e44]">{selectedSubmission.company}</p>
                        </div>
                      </div>
                    )}

                    <div className="flex items-start gap-3">
                      <Eye className="w-4 h-4 text-slate-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-slate-500">Inquiry Type</p>
                        <p className="text-sm font-medium text-[#1a2e44]">{getIntentLabel(selectedSubmission.intent)}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Building2 className="w-4 h-4 text-slate-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-slate-500">Property Type</p>
                        <p className="text-sm font-medium text-[#1a2e44]">{getPropertyTypeLabel(selectedSubmission.property_type)}</p>
                      </div>
                    </div>

                    {selectedSubmission.service && (
                      <div className="flex items-start gap-3">
                        <Wrench className="w-4 h-4 text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-500">Service Interest</p>
                          <p className="text-sm font-medium text-[#1a2e44]">{getServiceLabel(selectedSubmission.service)}</p>
                        </div>
                      </div>
                    )}

                    <div className="flex items-start gap-3 bg-slate-50 p-3 -mx-3 rounded">
                      <MapPin className="w-4 h-4 text-[#1a2e44] mt-0.5" />
                      <div>
                        <p className="text-xs text-slate-500">Lead Source</p>
                        <p className="text-sm font-bold text-[#1a2e44]">{getSourceLabel(selectedSubmission.source)}</p>
                      </div>
                    </div>

                    {selectedSubmission.message && (
                      <div className="flex items-start gap-3">
                        <MessageSquare className="w-4 h-4 text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-500">Message</p>
                          <p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedSubmission.message}</p>
                        </div>
                      </div>
                    )}

                    {selectedSubmission.location && selectedSubmission.location !== 'Location not available' && selectedSubmission.location !== 'Geolocation not supported' && (
                      <div className="flex items-start gap-3 bg-blue-50 p-3 -mx-3 rounded">
                        <MapPin className="w-4 h-4 text-blue-600 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-500">Form Location</p>
                          <a 
                            href={`https://www.google.com/maps?q=${selectedSubmission.location}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-medium text-blue-600 hover:underline"
                          >
                            View on Map ({selectedSubmission.location})
                          </a>
                        </div>
                      </div>
                    )}

                    {/* Attribution Data Section */}
                    {(selectedSubmission.utm_campaign || selectedSubmission.neighborhood || selectedSubmission.landing_page) && (
                      <div className="bg-amber-50 p-3 -mx-3 rounded border border-amber-200">
                        <p className="text-xs font-bold text-amber-800 mb-2">Campaign Attribution</p>
                        <div className="space-y-1 text-xs">
                          {selectedSubmission.neighborhood && (
                            <p><span className="text-slate-500">Neighborhood:</span> <span className="font-medium text-[#1a2e44]">{selectedSubmission.neighborhood}</span></p>
                          )}
                          {selectedSubmission.landing_page && (
                            <p><span className="text-slate-500">Landing Page:</span> <span className="font-medium text-[#1a2e44]">{selectedSubmission.landing_page}</span></p>
                          )}
                          {selectedSubmission.utm_source && (
                            <p><span className="text-slate-500">UTM Source:</span> <span className="font-medium text-[#1a2e44]">{selectedSubmission.utm_source}</span></p>
                          )}
                          {selectedSubmission.utm_medium && (
                            <p><span className="text-slate-500">UTM Medium:</span> <span className="font-medium text-[#1a2e44]">{selectedSubmission.utm_medium}</span></p>
                          )}
                          {selectedSubmission.utm_campaign && (
                            <p><span className="text-slate-500">UTM Campaign:</span> <span className="font-medium text-[#1a2e44]">{selectedSubmission.utm_campaign}</span></p>
                          )}
                          {selectedSubmission.referrer && (
                            <p><span className="text-slate-500">Referrer:</span> <span className="font-medium text-[#1a2e44] break-all">{selectedSubmission.referrer}</span></p>
                          )}
                          {selectedSubmission.lead_type && (
                            <p><span className="text-slate-500">Lead Type:</span> <span className="font-medium text-[#1a2e44]">{selectedSubmission.lead_type}</span></p>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="flex items-start gap-3 pt-3 border-t border-slate-200">
                      <Calendar className="w-4 h-4 text-slate-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-slate-500">Submitted</p>
                        <p className="text-sm text-slate-600">{formatDate(selectedSubmission.created_at)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-8 text-center text-slate-500">
                Select a submission to view details
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
